import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { meOptions, periodsOptions } from '../lib/resources/queries';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusPip, statusPipForSubmission } from '../components/ui/status-pip';
import { EmptyState } from '../components/ui/empty-state';
import { formatMoney, monthLabel } from '../lib/utils';
import { CurrentMonthCard } from './-components/current-month-card';
import type { PeriodSummary } from '../lib/resources/types';

export const Route = createFileRoute('/')({
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(meOptions()),
      queryClient.ensureQueryData(periodsOptions()),
    ]),
  component: DashboardPage,
});

function DashboardPage() {
  const meQuery = useSuspenseQuery(meOptions());
  const periodsQuery = useSuspenseQuery(periodsOptions());
  const me = meQuery.data;
  const periods = periodsQuery.data;

  // Selected period defaults to the calendar-current period (periods[0]). The user can
  // switch to any of the recent 12 — useful for finalizing a previous month after the
  // calendar has rolled over but before admin has locked it.
  const defaultId = periods[0]?.id ?? null;
  const [selectedId, setSelectedId] = useState<number | null>(defaultId);

  const selected = useMemo<PeriodSummary | null>(
    () => periods.find((p) => p.id === selectedId) ?? periods[0] ?? null,
    [periods, selectedId],
  );

  // History list = every period that isn't currently selected. Renders read-only
  // links + PDF download where applicable, so the user can still see the full
  // 12-month sweep at a glance.
  const history = periods.filter((p) => p.id !== selected?.id);

  if (!selected) {
    return (
      <div className="max-w-5xl mx-auto p-8">
        <Card>
          <EmptyState
            icon={FileText}
            title="No periods yet"
            description="The current month should auto-create on first load. Try refreshing."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-[28px] font-bold tracking-tight">Dashboard</h1>
        <label className="flex items-center gap-2 text-[12px] text-foreground-muted">
          <span>Working on</span>
          <select
            className="h-9 px-3 rounded-[--radius-input] border border-border bg-surface text-foreground text-[13px]"
            value={selected.id}
            onChange={(e) => setSelectedId(Number(e.target.value))}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {monthLabel(p.year, p.month)}
                {p.status === 'LOCKED' ? ' (locked)' : ''}
                {p.id === defaultId ? ' — current' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <CurrentMonthCard period={selected} currency={me.currency} />

      <Card>
        <CardHeader>
          <CardTitle>Other periods</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No other periods yet"
              description="Once a month rolls over, you'll see your past months here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {history.map((p) => {
                const sub = p.mySubmission;
                const total = sub?.status === 'FINALIZED' && sub.baseSalaryCentsSnapshot != null
                  ? sub.baseSalaryCentsSnapshot + sub.totalAmountCents
                  : null;
                return (
                  <li key={p.id} className="flex items-center gap-4 py-3 text-[13px]">
                    <button
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      className="font-medium w-40 text-left hover:text-primary"
                      title="Open this period"
                    >
                      {monthLabel(p.year, p.month)}
                    </button>
                    {p.status === 'LOCKED' && (
                      <StatusPip severity="info" label="Locked" />
                    )}
                    {sub ? (
                      <StatusPip {...statusPipForSubmission(sub.status)} />
                    ) : (
                      <span className="text-foreground-muted text-[12px] uppercase tracking-wide">no submission</span>
                    )}
                    {sub?.generatedInvoiceNumber && (
                      <span className="font-mono text-foreground-muted">{sub.generatedInvoiceNumber}</span>
                    )}
                    {total != null && (
                      <span className="font-mono ml-auto">{formatMoney(total, me.currency)}</span>
                    )}
                    {sub?.status === 'FINALIZED' && (
                      <Button asChild variant="ghost" size="sm">
                        <a href={`/api/files/contractor-invoice/${sub.id}/download`} target="_blank" rel="noreferrer">
                          <Download className="size-3.5" /> PDF
                        </a>
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
